import { useCallback, useSyncExternalStore } from "react";

/**
 * The wall clock, as something a component may read while it renders.
 *
 * Calling `Date.now()` in a render body hands two renders of the same paint two different
 * answers, so the number that gets committed need not be the number that was computed. The
 * instant comes from here instead, where it is ordinary state that changes on a tick.
 *
 * `step` is both the resolution and the wake-up rate, because they are the same decision: a
 * row drawing "3 minutes ago" has no use for a clock that moves every frame, and flooring to
 * the step means the component only re-renders when the number it actually prints has
 * changed. Callers sharing a step share one interval, so they also always agree.
 */

interface Ticker {
  /** `Date.now()` floored to the step, which is the only value subscribers ever see. */
  now: number;
  listeners: Set<() => void>;
  timer: number | undefined;
}

// Keyed by step and populated as components mount, so a Map rather than a lookup table.
const tickers = new Map<number, Ticker>();

function tickerFor(step: number): Ticker {
  let ticker = tickers.get(step);
  if (!ticker) {
    ticker = { now: Math.floor(Date.now() / step) * step, listeners: new Set(), timer: undefined };
    tickers.set(step, ticker);
  }
  return ticker;
}

/** Re-reads the clock and wakes every subscriber, but only when the floored value moved. */
function refresh(ticker: Ticker, step: number): void {
  const next = Math.floor(Date.now() / step) * step;
  if (next === ticker.now) return;
  ticker.now = next;
  for (const listener of ticker.listeners) listener();
}

export function useNow(step: number): number {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const ticker = tickerFor(step);
      ticker.listeners.add(onStoreChange);
      // A ticker whose last subscriber left an hour ago is still holding an hour-old instant,
      // and the interval below would not correct it until a whole step had passed. Everyone
      // is woken and not just the arrival: two components on one step disagreeing about the
      // time is the thing this hook exists to prevent.
      refresh(ticker, step);
      ticker.timer ??= window.setInterval(() => refresh(ticker, step), step);
      return () => {
        ticker.listeners.delete(onStoreChange);
        if (ticker.listeners.size) return;
        window.clearInterval(ticker.timer);
        ticker.timer = undefined;
      };
    },
    [step],
  );

  const snapshot = useCallback(() => tickerFor(step).now, [step]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
